import React, { useState } from "react";
import { ScrollText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { fmtDate, fmtTime } from "@/components/PageHeader";
import { shortNo } from "@/lib/caseNo";

export function hasConference(m) {
  return m.acknowledgement_choice === "conference_requested" || !!m.conference_date || !!m.conference_outcome;
}

function fmtDT(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", { timeZone: "America/Chicago", year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function ConferenceLog({ caseItem: m, size = "sm" }) {
  const [open, setOpen] = useState(false);
  const outcome = m.conference_outcome || (m.status === "rescinded" ? "dropped" : null);
  const outcomeAt = m.conference_outcome_at || (m.status === "rescinded" ? m.rescinded_at : null);
  const outcomeLabel = outcome === "upheld"
    ? "Verdict: upheld — points now count"
    : outcome === "dropped"
      ? "Verdict: dropped — case rescinded"
      : "Verdict — awaiting";

  const log = m.conference_log || [];
  const firstSet = log.find((e) => e.type === "scheduled");
  const reschedules = log.filter((e) => e.type === "rescheduled");
  const confWhen = (dt, t) => `${fmtDate(dt)}${t ? ` at ${fmtTime(t)}` : ""}`;

  const steps = [
    { key: "issued", label: "Merit issued", display: fmtDate(m.date_issued), note: m.description, done: true },
    {
      key: "acknowledged", label: "Case acknowledged",
      display: m.acknowledgement_date ? fmtDT(m.acknowledgement_date) : null,
      note: m.acknowledgement_choice === "conference_requested"
        ? "Member requested a conference"
        : (m.acknowledgement_submitted ? "No further action requested" : null),
      done: !!m.acknowledgement_submitted,
    },
  ];
  if (firstSet) {
    steps.push({ key: "set", label: "Meeting set", display: confWhen(firstSet.date, firstSet.time), note: `Set on ${fmtDT(firstSet.at)}`, done: true });
  } else {
    steps.push({ key: "set", label: "Meeting set", display: m.conference_date ? confWhen(m.conference_date, m.conference_time) : null, done: !!m.conference_date });
  }
  reschedules.forEach((e, i) => {
    steps.push({
      key: `resched-${i}`, label: "Meeting rescheduled",
      display: confWhen(e.date, e.time),
      note: `${e.from ? `Moved from ${fmtDate(e.from)} · ` : ""}on ${fmtDT(e.at)}`,
      done: true,
    });
  });
  steps.push({ key: "outcome", label: outcomeLabel, display: outcomeAt ? fmtDT(outcomeAt) : null, note: m.conference_notes, done: !!outcome });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size={size} variant="outline" className="rounded-full" data-testid={`conference-log-btn-${m.case_number}`}>
          <ScrollText className="mr-1 h-3.5 w-3.5" /> Log
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="conference-log-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Conference log · {shortNo(m.case_number)}</DialogTitle>
        </DialogHeader>
        <ol className="relative ml-2 space-y-5 border-l border-slate-200 pl-6">
          {steps.map((s) => (
            <li key={s.key} className="relative" data-testid={`conf-log-step-${s.key}`}>
              <span className={`absolute -left-[31px] top-0.5 flex h-4 w-4 items-center justify-center rounded-full ${s.done ? "bg-slate-900" : "bg-slate-200"}`}>
                <span className="h-1.5 w-1.5 rounded-full bg-white" />
              </span>
              <p className={`text-sm font-semibold ${s.done ? "text-slate-800" : "text-slate-400"}`}>{s.label}</p>
              <p className="text-xs text-slate-500">{s.done ? (s.display || fmtDT(s.date)) : "Pending"}</p>              {s.note && s.done && (
                <p className="mt-1 rounded-md bg-slate-50 px-2 py-1 text-xs text-slate-600" data-testid={`conf-log-note-${s.key}`}>{s.note}</p>
              )}
            </li>
          ))}
        </ol>
      </DialogContent>
    </Dialog>
  );
}
